import React, { useEffect } from 'react';
import { Button, Container, Typography, Box, CircularProgress, useTheme } from '@mui/material';
import PageTitle from './PageTitle';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function Login() {
  const { signInWithGoogle, user, loading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    if (user) {
      navigate('/account');
    }
  }, [user, navigate]);

  const handleLogin = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Error signing in:', error);
    }
  };

  // If loading, show a simple spinner in the center
  if (loading) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18,18,18,1)' : 'rgba(255,255,255,1)'
        }}
      >
        <CircularProgress sx={{ color: '#CAF84C' }} />
      </Box>
    );
  }

  // If user is already signed in, they will be redirected by the useEffect
  // But just in case, show a quick message
  if (user) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={3}
        p={3}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18,18,18,1)' : 'rgba(255,255,255,1)'
        }}
      >
        <Typography 
          variant="h6" 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontFamily: '"Inter", sans-serif',
            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'inherit'
          }}
        >
          Successfully signed in! Redirecting...
        </Typography>
        <CircularProgress size={24} sx={{ color: '#CAF84C' }} />
      </Box>
    );
  }

  return (
    <>
      <PageTitle title="Cover Letter Generator" />
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          minHeight: '100vh',
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, rgba(18,18,18,1) 50%, rgba(30,30,30,1) 100%)'
              : 'linear-gradient(135deg, rgba(255,255,255,1) 50%, rgba(240,240,240,1) 100%)',
          display: 'flex',
          flexDirection: 'column',
          color: theme.palette.text.primary
        }}
      >
        <Box
          flex="1"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          py={8}
          px={2}
        >
          {/* Logo */}
          <Box
            mb={4}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%'
            }}
          >
            <img
              src="/HandshakeLogo.png" // Ensure the image is being loaded from the public folder
              alt="QCL Logo"
              style={{
                maxWidth: '180px',
                height: 'auto',
                borderRadius: '8px'
              }}
            />
          </Box>
          
          {/* Main Headline */}
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            mb={2}
            sx={{ maxWidth: '700px', fontFamily: '"Inter", sans-serif' }}
          >
            Let's Begin
          </Typography>

          {/* Sub-Headline / Tagline */}
          <Typography
            variant="h6"
            color="text.secondary"
            mb={4}
            sx={{ 
              maxWidth: '500px', 
              fontFamily: '"Inter", sans-serif',
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary'
            }}
          >
            Big mistakes cost offers. Small mistakes cost interviews. Generate perfect
            cover letters instantly.
          </Typography>

          {/* Call to Action */}
          <Button
            variant="contained"
            size="large"
            onClick={handleLogin}
            startIcon={<GoogleIcon />}
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? '#fff' : '#000',
              color: theme.palette.mode === 'dark' ? '#000' : '#fff',
              padding: '12px 24px',
              fontFamily: '"Inter", sans-serif',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : '#333'
              }
            }}
          >
            Sign in with Google
          </Button>
        </Box>
      </Container>
    </>
  );
}
